import { setCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useState } from "react";
import { css } from "styled-components";

export interface ShippingConsentModalStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    shippingWarningGridItem?: GridItemProps;
    shippingWarningText?: TypographyPresentationProps;
    bottomRowGridItem?: GridItemProps;
    buttonsContainer?: GridContainerProps;
    consentTermsGridItem?: GridItemProps;
    consentTermsCheckbox?: CheckboxPresentationProps;
    acceptButtonGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
}

export const shippingConsentModalStyles: ShippingConsentModalStyles = {
    modal: {
        size: 700,
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    container: { gap: 20 },
    shippingWarningGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    bottomRowGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    buttonsContainer: {
        gap: 20,
        css: css`
            & ${GridOffset} {
                justify-content: flex-end;
            }
        `,
    },
    consentTermsGridItem: { width: 12 },
    consentTermsCheckbox: {
        css: css`
            cursor: pointer;
        `
    },
    acceptButtonGridItem: {
        width: [12, 3, 2, 3, 3],
        css: css`
            justify-content: flex-end;
        `,
    },
    acceptButton: {
        variant: "primary",
        css: css`
            width: 100%;
        `,
    },
};

interface ShippingTermsConsentModalProps {
    extendedStyles?: ShippingConsentModalStyles;
    isModalOpen: boolean;
    onClose: any;
}

const ShippingTermsConsentModal = ({
    extendedStyles,
    isModalOpen,
    onClose
}: ShippingTermsConsentModalProps) => {

    const [styles] = useState(() => mergeToNew(shippingConsentModalStyles, extendedStyles));
    const [isConsented, setIsConsented] = useState(false);

    const consentChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsConsented(value);
    };

    const handleAcceptTerms = () => {
        setCookie("ShippingTermsAccepted", "true");
        onClose();
    }

    return (
        <Modal
            {...styles.modal}
            isOpen={isModalOpen}
            handleClose={onClose}
            headline={siteMessage("ClassifiedShippingTermsTitleText")}
        >
            <GridContainer {...styles.container} data-test-selector="ShippingAcceptForm">
                <GridItem {...styles.shippingWarningGridItem}>
                    <Typography {...styles.shippingWarningText}>
                        {siteMessage("ClassifiedShippingTermsText")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.consentTermsGridItem}>
                    <Checkbox
                        {...styles.consentTermsCheckbox}
                        checked={isConsented}
                        onChange={consentChangeHandler}
                        data-test-selector="consentShippingTerms"
                    >
                        {siteMessage("ClassifiedShippingTermsAcknowledgementText")}
                    </Checkbox>
                </GridItem>
                <GridItem {...styles.bottomRowGridItem}>
                    <GridContainer {...styles.buttonsContainer}>
                        <GridItem {...styles.acceptButtonGridItem}>
                            <Button
                                {...styles.acceptButton}
                                onClick={handleAcceptTerms}
                                data-test-selector="acceptButton"
                                disabled={!isConsented}
                            >
                                {translate("Accept")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </Modal>
    );
};

export default ShippingTermsConsentModal;